/**
 * /workspaces/* endpoints — create + read + admin management.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getApiClient } from '@clarix/api-client';
import { useAuthStore } from '@clarix/auth-store';
import type {
  CreateWorkspaceRequest,
  CreateWorkspaceResponse,
  SelfServeCreateWorkspaceRequest,
  SelfServeCreateWorkspaceResponse,
  ProvisioningStatusResponse,
  WorkspaceDetails,
  WorkspaceAppRow,
  ToggleAppRequest,
  WorkspaceMember,
  MeWorkspace,
  JwtPayload,
  CheckSlugResponse,
  OnboardingStateResponse,
  OnboardingPatchPayload,
  OnboardingPatchResponse,
  SkipBrandingResponse,
  UploadLogoResponse,
} from '@clarix/shared-types';
import { unwrap, type ApiResponse } from './envelope';
import { queryKeys } from './keys';

function decodeJwt(token: string): JwtPayload | null {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    return JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/'))) as JwtPayload;
  } catch {
    return null;
  }
}

/**
 * POST /workspaces/claim — an already-authenticated, workspace-less caller
 * claims their first workspace; re-issues the JWT bound to it.
 *
 * Distinct from PUBLIC POST /workspaces (self-serve signup, no session yet —
 * see useSelfServeCreateWorkspace below): this one is for OnboardingPage.tsx,
 * reached only by an existing session with zero workspaces.
 */
export function useCreateWorkspace() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateWorkspaceRequest): Promise<CreateWorkspaceResponse> => {
      const res = await getApiClient().post<ApiResponse<CreateWorkspaceResponse>>(
        '/workspaces/claim',
        body,
      );
      const data = unwrap(res.data);
      const claims = decodeJwt(data.access_token);
      useAuthStore.getState().setSession({
        token: data.access_token,
        user: claims,
        workspaceSlug: data.workspace.slug,
      });
      qc.invalidateQueries({ queryKey: queryKeys.me() });
      qc.invalidateQueries({ queryKey: queryKeys.myWorkspaces() });
      return data;
    },
  });
}

/**
 * POST /workspaces — PUBLIC self-serve signup, no session yet. Triggers real
 * tenant provisioning and returns 202 immediately; the caller polls
 * useProvisioningStatus below until it leaves PROVISIONING, then logs in
 * normally (no session is issued here — the owner account is created inside
 * the new tenant database, not this process).
 */
export function useSelfServeCreateWorkspace() {
  return useMutation({
    mutationFn: async (
      body: SelfServeCreateWorkspaceRequest,
    ): Promise<SelfServeCreateWorkspaceResponse> => {
      const res = await getApiClient().post<ApiResponse<SelfServeCreateWorkspaceResponse>>(
        '/workspaces',
        body,
      );
      return unwrap(res.data);
    },
  });
}

/**
 * GET /workspaces/:slug/provisioning-status — PUBLIC, one fresh read per
 * call, no caching. Poll this (client-side interval) while status stays
 * PROVISIONING; enable only once a slug from useSelfServeCreateWorkspace
 * exists, and stop polling once status leaves PROVISIONING.
 */
export function useProvisioningStatus(
  slug: string | null,
  opts: { enabled: boolean; intervalMs?: number },
) {
  return useQuery({
    queryKey: slug ? queryKeys.provisioningStatus(slug) : ['workspace', '__nil__', 'provisioning-status'],
    queryFn: async (): Promise<ProvisioningStatusResponse> => {
      const res = await getApiClient().get<ApiResponse<ProvisioningStatusResponse>>(
        `/workspaces/${slug}/provisioning-status`,
      );
      return unwrap(res.data);
    },
    enabled: opts.enabled && !!slug,
    staleTime: 0,
    retry: false,
    refetchInterval: (query) =>
      query.state.data?.status === 'PROVISIONING' ? (opts.intervalMs ?? 2500) : false,
  });
}

/** GET /me/workspaces — list user's workspaces. */
export function useMyWorkspaces() {
  const hasToken = useAuthStore((s) => !!s.token);
  return useQuery({
    queryKey: queryKeys.myWorkspaces(),
    queryFn: async (): Promise<MeWorkspace[]> => {
      const res = await getApiClient().get<ApiResponse<MeWorkspace[]>>('/me/workspaces');
      return unwrap(res.data);
    },
    enabled: hasToken,
    staleTime: 60_000,
  });
}

/** GET /workspaces/:slug */
export function useWorkspaceBySlug(slug: string | null) {
  return useQuery({
    queryKey: slug ? queryKeys.workspaceBySlug(slug) : ['workspace', '__nil__'],
    queryFn: async (): Promise<WorkspaceDetails> => {
      const res = await getApiClient().get<ApiResponse<WorkspaceDetails>>(`/workspaces/${slug}`);
      return unwrap(res.data);
    },
    enabled: !!slug,
    staleTime: 30_000,
  });
}

/** GET /workspaces/:slug/admin/apps */
export function useWorkspaceApps(slug: string | null) {
  return useQuery({
    queryKey: slug ? queryKeys.workspaceApps(slug) : ['workspace', '__nil__', 'apps'],
    queryFn: async (): Promise<WorkspaceAppRow[]> => {
      const res = await getApiClient().get<ApiResponse<WorkspaceAppRow[]>>(
        `/workspaces/${slug}/admin/apps`,
      );
      return unwrap(res.data);
    },
    enabled: !!slug,
  });
}

/** PUT /workspaces/:slug/admin/apps/:code */
export function useToggleApp(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { code: string; body: ToggleAppRequest }) => {
      const res = await getApiClient().put<
        ApiResponse<{ access_token?: string } & Record<string, unknown>>
      >(`/workspaces/${slug}/admin/apps/${input.code}`, input.body);
      const data = unwrap(res.data);
      // The backend re-issues the JWT (with refreshed app_access) when we toggle
      // an app for our own active workspace. Adopt it right away so the enabled
      // app loads on first visit instead of requiring a logout/login.
      if (data?.access_token) {
        const claims = decodeJwt(data.access_token);
        useAuthStore.getState().setSession({
          token: data.access_token,
          user: claims,
          workspaceSlug: claims?.workspace_slug ?? null,
        });
      }
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.workspaceApps(slug) });
      qc.invalidateQueries({ queryKey: queryKeys.me() });
    },
  });
}

/** GET /workspaces/:slug/admin/members */
export function useWorkspaceMembers(slug: string | null, page = 1, per_page = 50) {
  return useQuery({
    queryKey: slug ? queryKeys.workspaceMembers(slug, page) : ['workspace', '__nil__', 'members'],
    queryFn: async (): Promise<{ items: WorkspaceMember[]; total: number }> => {
      const res = await getApiClient().get<ApiResponse<WorkspaceMember[]>>(
        `/workspaces/${slug}/admin/members`,
        { params: { page, per_page } },
      );
      const data = unwrap(res.data);
      const total = res.data.success ? (res.data.pagination?.total ?? data.length) : data.length;
      return { items: data, total };
    },
    enabled: !!slug,
  });
}

/** PATCH /workspaces/:slug/admin/members/:userId/role */
export function useChangeMemberRole(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { userId: number; role: 'ADMIN' | 'MEMBER' }) => {
      const res = await getApiClient().patch<ApiResponse<unknown>>(
        `/workspaces/${slug}/admin/members/${input.userId}/role`,
        { role: input.role },
      );
      return unwrap(res.data);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['workspace', slug, 'members'] }),
  });
}

/** PATCH /workspaces/:slug/admin/members/:userId/status */
export function useChangeMemberStatus(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      userId: number;
      status: 'ACTIVE' | 'PAUSED' | 'SUSPENDED';
      reason?: string;
    }) => {
      const res = await getApiClient().patch<ApiResponse<unknown>>(
        `/workspaces/${slug}/admin/members/${input.userId}/status`,
        { status: input.status, reason: input.reason },
      );
      return unwrap(res.data);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['workspace', slug, 'members'] }),
  });
}

/** DELETE /workspaces/:slug/admin/members/:userId */
export function useRemoveMember(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (userId: number) => {
      await getApiClient().delete(`/workspaces/${slug}/admin/members/${userId}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['workspace', slug, 'members'] }),
  });
}

// -----------------------------------------------------------------------------
// Onboarding wizard hooks
// See spec docs/superpowers/specs/2026-05-21-workspace-onboarding-wizard-design.md
// -----------------------------------------------------------------------------

/**
 * POST /workspaces/check-slug — debounced live availability check used in
 * the wizard step-1 form. Caller is expected to wrap this in their own
 * debounce; React Query's cache de-dupes identical concurrent calls.
 */
export function useCheckSlug(slug: string) {
  return useQuery({
    queryKey: queryKeys.slugCheck(slug),
    queryFn: async (): Promise<CheckSlugResponse> => {
      const res = await getApiClient().post<ApiResponse<CheckSlugResponse>>(
        '/workspaces/check-slug',
        { slug },
      );
      return unwrap(res.data);
    },
    // Slug uniqueness can change between checks; never serve stale.
    staleTime: 0,
    enabled: slug.length >= 2,
  });
}

export interface ResolveWorkspaceResponse {
  exists: boolean;
  /** Signed in to right now. False for suspended / archived / still provisioning. */
  active: boolean;
  slug: string | null;
  /** Only present when `active` — the backend withholds it otherwise. */
  name: string | null;
}

/**
 * GET /workspaces/resolve?slug= — PUBLIC, the apex workspace picker's lookup.
 *
 * Deliberately NOT `useCheckSlug`: that one is behind the auth wall and its
 * polarity is inverted (`available: true` means the workspace does NOT exist).
 * This asks the opposite question — is there a workspace here I can sign in to —
 * and is callable before anyone has a token.
 */
export function useResolveWorkspace(slug: string) {
  return useQuery({
    queryKey: queryKeys.workspaceResolve(slug),
    queryFn: async (): Promise<ResolveWorkspaceResponse> => {
      const res = await getApiClient().get<ApiResponse<ResolveWorkspaceResponse>>(
        `/workspaces/resolve?slug=${encodeURIComponent(slug)}`,
      );
      return unwrap(res.data);
    },
    // A workspace can be suspended between attempts; never serve a stale verdict.
    staleTime: 0,
    retry: false,
    enabled: slug.length >= 2,
  });
}

/** GET /workspaces/:slug/onboarding — hydrate wizard with current state + filled fields. */
export function useOnboarding(slug: string | null) {
  return useQuery({
    queryKey: slug ? queryKeys.workspaceOnboarding(slug) : ['workspace', '__nil__', 'onboarding'],
    queryFn: async (): Promise<OnboardingStateResponse> => {
      const res = await getApiClient().get<ApiResponse<OnboardingStateResponse>>(
        `/workspaces/${slug}/onboarding`,
      );
      return unwrap(res.data);
    },
    enabled: !!slug,
    // No background refetch — the only writer is this user via PATCH below,
    // and we invalidate manually on every successful mutation.
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });
}

/**
 * PATCH /workspaces/:slug/onboarding — advances the wizard one step.
 * The backend dispatches on the workspace's *current* state, so the caller
 * just sends the step-appropriate payload shape.
 */
export function usePatchOnboarding(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: OnboardingPatchPayload): Promise<OnboardingPatchResponse> => {
      const res = await getApiClient().patch<ApiResponse<OnboardingPatchResponse>>(
        `/workspaces/${slug}/onboarding`,
        payload,
      );
      return unwrap(res.data);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.workspaceOnboarding(slug) });
      qc.invalidateQueries({ queryKey: queryKeys.workspaceBySlug(slug) });
    },
  });
}

/** POST /workspaces/:slug/onboarding/skip-branding — DONE without setting branding fields. */
export function useSkipBranding(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<SkipBrandingResponse> => {
      const res = await getApiClient().post<ApiResponse<SkipBrandingResponse>>(
        `/workspaces/${slug}/onboarding/skip-branding`,
      );
      return unwrap(res.data);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.workspaceOnboarding(slug) });
      qc.invalidateQueries({ queryKey: queryKeys.me() });
    },
  });
}

/**
 * POST /workspaces/:slug/logo (multipart). Returns the stored CDN/static URL.
 * The caller is responsible for then PATCHing the workspace BRANDING step
 * with the returned logo_url.
 */
export function useUploadLogo(slug: string) {
  return useMutation({
    mutationFn: async (file: File): Promise<UploadLogoResponse> => {
      const fd = new FormData();
      fd.append('file', file);
      const res = await getApiClient().post<ApiResponse<UploadLogoResponse>>(
        `/workspaces/${slug}/logo`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      return unwrap(res.data);
    },
  });
}
